import {z} from 'zod';
import {isObjectId} from '@packages/utilities/bson-utils';

/**
 * Dynamic description generators for database IDs.
 * Generates appropriate descriptions based on ID length and type.
 */
export const ID_DESCRIPTIONS = {
  GENERIC: (length: number) => {
    const idType = length === 24 ? 'MongoDB ObjectId' : 'database ID';
    return `A valid ${idType} (${length}-character hexadecimal string). Use the actual database ID, not a human-readable name.`;
  },

  DETAILED: (length: number) => {
    const idType = length === 24 ? 'MongoDB ObjectId' : 'database ID';
    const example = generateIdExample(length);
    return `A valid ${idType} - a ${length}-character hexadecimal string (e.g., "${example}"). Always use the actual database ID retrieved from previous queries, never use display names or slugs.`;
  },

  ERROR_MESSAGE: (length: number) => {
    const idType = length === 24 ? 'MongoDB ObjectId' : 'database ID';
    const example = generateIdExample(length);
    return `Invalid ${idType}. Must be a ${length}-character hexadecimal string (e.g., "${example}"). Use the actual database ID, not a display name.`;
  },
} as const;

/**
 * Generates hexadecimal ID examples based on length using a single realistic base string
 */
const generateIdExample = (length: number): string => {
  // Single realistic 32-character hex string
  const baseHex = 'd99f2b9ee4da1f7c7e506a7c7ac1e8f6';

  if (length <= 32) {
    // Use substring for lengths up to 32
    return baseHex.substring(0, length);
  }

  // For lengths > 32, cycle through the base string
  let result = '';
  for (let i = 0; i < length; i++) {
    result += baseHex[i % 32];
  }

  return result;
};

/**
 * Creates an id schema (MongoDB ObjectId if 24 character) with a custom description for specific entity types
 * @param entityType - The type of entity this ID represents (e.g., "CMS collection", "user", "site")
 * @param useDetailedDescription - Whether to use the detailed description with examples
 * @param customDescription - Optional custom description to override the default
 * @param length - The expected length of the ID (default: 24 for MongoDB ObjectIds)
 * @returns A zod schema for validating IDs
 */
export const createIdSchema = (
  entityType: string,
  useDetailedDescription = false,
  customDescription?: string,
  length = 24
) => {
  const baseDescription = useDetailedDescription
    ? ID_DESCRIPTIONS.DETAILED(length)
    : ID_DESCRIPTIONS.GENERIC(length);

  const finalDescription =
    customDescription || `The id of the ${entityType}. ${baseDescription}`;

  return z
    .string()
    .min(length, `ID must be exactly ${length} characters`)
    .max(length, `ID must be exactly ${length} characters`)
    .refine((val) => length !== 24 || isObjectId(val), {
      message: `Invalid ID for ${entityType}. ${ID_DESCRIPTIONS.ERROR_MESSAGE(length)}`,
    })
    .describe(finalDescription);
};

/**
 * Reusable schemas for common entity types for the data tools
 */
export const dataToolSchemas = {
  collectionId: createIdSchema('CMS collection', true),
  itemId: createIdSchema('CMS collection item', true),
  fieldId: createIdSchema('CMS collection field', true, undefined, 32),
  siteId: createIdSchema('site', true),
  workspaceId: createIdSchema('workspace', true),
  userId: createIdSchema('user', true),
  pageId: createIdSchema('page', true),
  localeId: createIdSchema(
    'CMS locale',
    false,
    'Unique identifier for a CMS Locale. To query multiple locales, input a comma separated string.'
  ),
  // DOM and component-related IDs (UUIDs)
  nodeId: z
    .string()
    .uuid()
    .describe(
      'The unique UUID identifier of a DOM node on the page (e.g., "79c0e560-f751-2262-fed0-30f9b2a11868")'
    ),
  propertyId: z
    .string()
    .uuid()
    .describe(
      'The unique UUID identifier of a component property (e.g., "50486119-874a-b334-4bf8-647b8a39c851")'
    ),
} as const;
